import type { ProformApiCommandQuerySettings } from '@eliadmin/core/src/api/proform/proform-api';
import type { ProductConfId, ProductDetail } from '@eliadmin/core/src/api/proform/proform-types';
import {
  ORIENTATION_KEYS,
  ORIENTATION_TO_LABEL,
  PLACING_KEYS,
  PLACING_TO_LABEL,
} from '@eliadmin/core/src/components/configurator/configurator-constants';
import type { EnhancedProductConf } from '@eliadmin/core/src/stores/configurator-store';
import {
  type ConfiguratorState,
  type ProductConfIdColumn,
} from '@eliadmin/core/src/stores/configurator-store';
import type { StringOrNumber } from '@eliadmin/core/src/types/misc-types';

export function isEmpty(value: string | null | undefined) {
  return value === null || value === undefined || value === '';
}

export function extractDataFromField(
  column: ProductConfIdColumn,
  conf: ProformApiCommandQuerySettings<'GET_PRODUCT_CONF'>['response'],
) {
  const smakoKey = column.replace('user', 'smako_conf');
  const plainKey = smakoKey.replace('smako_conf_', '');

  // @ts-expect-error TODO: fix
  const value = (conf.root[smakoKey]?.[plainKey] ?? null) as FieldDataItem | FieldDataItem[] | null;

  if (!value) return null;

  return Array.isArray(value) ? value : [value];
}

export function parseProformNumber(value: string | number | null | undefined) {
  if (value === null || value === undefined || value === '') {
    return Number.NaN;
  }

  return Number(Number(String(value).replace(',', '.')).toFixed(2));
}

export function getProformPreviewUrl(confId: ProductConfId, detail: ProductDetail): string {
  let url: string = '';

  const parameters = confId.root.smako_conf.column;
  if (detail.root.smako_url_nahed && detail.root.smako_url_nahed.url.url_nahled !== '') {
    url = detail.root.smako_url_nahed.url.url_nahled;
    url += '/Wysokość/' + confId.root.smako_conf.column.user_vyska;
    url += '/Szerokość/' + parameters.user_sirka;

    for (let i = 1; i <= 9; i++) {
      const propName = `user_m${i}`;
      const propO = `user_m${i}_o`;
      const propX = `user_m${i}_x`;
      const propY = `user_m${i}_y`;

      let userM = parameters[propName as ProductConfIdColumn];
      const userMO = parameters[propO as ProductConfIdColumn];
      const userMX = parameters[propX as ProductConfIdColumn];
      const userMY = parameters[propY as ProductConfIdColumn];

      // THIS is just a weird hack we have to do because of the way the API works
      userM = userM === 'ZV' ? 'struktura' : userM;

      if (userM !== '') {
        url += `/Ornament ${i}/` + userM.replace('/', '_');

        if (userMO !== '' && userMO !== '0') {
          url += `/Ornament ${i} obrót/` + userMO;
        }

        if (userMX !== '') {
          url += `/Ornament ${i} M1/` + userMX;
        }

        if (userMY !== '') {
          url += `/Ornament ${i} M2/` + userMY;
        }
      }
    }

    url += '/linie/1';
  }

  return encodeURI(url);
}

export function getIdNomenklaturyFrom(product: { code: string }) {
  return product.code;
}

export function buildProductCode(id_nomen: StringOrNumber, conf_id: StringOrNumber) {
  return `${id_nomen}::${conf_id}`;
}
export function parseProductCode(code: string) {
  const [id_nomen, conf_id] = code.split('::');
  return { id_nomen, conf_id };
}

export function getProformImageUrl(relativePath: string) {
  return `https://apieshop.smako.cz/files/data/${relativePath}`;
}

export function extractProformArray<T>(data: T[] | T | undefined): T[] {
  return Array.isArray(data) ? data : data ? [data] : [];
}

export const GET_ATTRIBUTES_FROM_CONF_IGNORE_FIELDS: (keyof ProductConfId['root']['smako_conf']['column'])[] =
  [
    'e_mail',
    'cena',
    'cislo_nomenklatury',
    'mnozstvi',
    'id_nomen',
    'id_meny',
    'sirka_max',
    'sirka_min',
    'vyska_max',
    'vyska_min',
    'user_poznamka',
  ];
export function getAttributesFromConf(
  conf: ProductConfId,
  cols: ConfiguratorState['productCols'],
  useLabels = true,
  fieldsToIgnore: (keyof ProductConfId['root']['smako_conf']['column'])[] = GET_ATTRIBUTES_FROM_CONF_IGNORE_FIELDS,
  t: (key: string) => string = key => key,
  confEnhanced?: EnhancedProductConf,
) {
  const attributes: { name: string; attribute_name: string }[] = [];

  const colsMap = cols.root.smako_conf_cols?.column.reduce(
    (acc, col) => {
      acc[col.column] = col;
      return acc;
    },
    {} as Record<string, (typeof cols)['root']['smako_conf_cols']['column'][number]>,
  );

  for (const [key, value] of Object.entries(conf.root.smako_conf.column) as [
    keyof ProductConfId['root']['smako_conf']['column'],
    string,
  ][]) {
    if (fieldsToIgnore.includes(key as keyof ProductConfId['root']['smako_conf']['column']))
      continue;
    if (!value || value === '0') continue;

    let name = value;

    if (useLabels) {
      if (ORIENTATION_KEYS.includes(key as any)) {
        name = t(ORIENTATION_TO_LABEL[value as keyof typeof ORIENTATION_TO_LABEL] ?? value);
      } else if (PLACING_KEYS.includes(key as any)) {
        name = t(PLACING_TO_LABEL[value as keyof typeof PLACING_TO_LABEL] ?? value);
      }
    }

    const fieldData = extractDataFromField(key as ProductConfIdColumn, confEnhanced as any) ?? null;
    const description = fieldData?.find(item => item.kod === value)?.popis ?? '';

    colsMap[key]?.basket === '1' &&
      attributes.push({
        attribute_name: useLabels ? (colsMap[key]?.label ?? key) : key,
        name: name + ' ' + description,
      });
  }

  return attributes;
}

// some proform slugs can contain / make sure that we replace it with something else eg. ___
export function encodeProformSlug(slug: string) {
  return slug.replace('/', '___');
}

export function decodeProformSlug(slug: string) {
  return slug.replace('___', '/');
}

export function downloadBase64File(base64Data: string, fileName: string) {
  // Decode the base64 string to binary data
  const binaryData = atob(base64Data);

  // Convert binary data to Uint8Array
  const uint8Array = new Uint8Array(binaryData.length);
  for (let i = 0; i < binaryData.length; i++) {
    uint8Array[i] = binaryData.charCodeAt(i);
  }

  // Determine the MIME type based on the file extension
  const fileExtension = fileName.split('.').pop()?.toLowerCase() || 'pdf';
  let mimeType = 'application/pdf'; // Default MIME type is PDF

  const mimeTypes: Record<string, string> = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    txt: 'text/plain',
  };

  if (fileExtension in mimeTypes) {
    mimeType = mimeTypes[fileExtension];
  }

  // Create a Blob with the correct MIME type
  const blob = new Blob([uint8Array], { type: mimeType });

  // Create a URL for the Blob
  const url = URL.createObjectURL(blob);

  // Create a temporary anchor element and trigger the download
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName.includes('.') ? fileName : `${fileName}.pdf`;
  document.body.appendChild(a);
  a.click();

  // Clean up
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
